import { MATCH_STATE } from './machine';
import { ARTIFACT_PHASE } from '../types';
import type {
  ArtifactPhase,
  Foot,
  HypothesisRow,
  MatchRow,
  MatchState,
  Status,
  TemplateEntry,
} from '../types';

/**
 * FILE-1 … FILE-3 / SES-11 / PRE-6 — **the opponent file's decisions**, all of them (build unit U-16).
 *
 * §1.2 keeps `ui/` away from `data/` and keeps derivations out of both. Everything this file
 * exports answers a question the Home screen and the Team view would otherwise each answer for
 * themselves — which is how two surfaces come to disagree about the same match.
 *
 * ── ENV-11, made unrepresentable rather than remembered ─────────────────────
 *
 * *"Player identification is by shirt number only. No player names are stored anywhere."*
 *
 * `PlayerRow` already has no name column (`types/index.ts`). That is the storage half. The half a
 * **browser** could still get wrong is the projection: an opponent file is exactly the screen where
 * a "display name", a "label" or a "who is this" field would be added by someone being helpful, and
 * nothing in a type-checker objects to a new optional string. {@link PlayerLink} is therefore a
 * **closed** key set — {@link PLAYER_LINK_FIELDS} proves every listed key is a field and
 * {@link PlayerLinkFieldsAreClosed} proves every field is listed, in the same two-way shape
 * `SETTING_KEYS` uses for §11.8's deny-list. Adding a name to the projection does not compile until
 * someone writes the word into a list that says what it is for.
 *
 * ── SES-6, which is a NEGATIVE and therefore lives in one predicate ──────────
 *
 * *"Abandoned matches … MUST NOT appear in the pending-distillation queue (SES-11) unless the user
 * opens one explicitly."* {@link isPendingDistillation} is the only place that decides queue
 * membership, so the exclusion cannot be true on one surface and false on another.
 */

// ─────────────────────────────────────────────────────────────────────────────
// FILE-2 / FILE-3 / ENV-11 — the player projection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * FILE-3 — *"the Player row is reachable from a hypothesis and shows only its number, foot and
 * notes."* That sentence is this type.
 *
 * `id` is deliberately **absent**. The Team view keys its player rows on the shirt number, which is
 * unique per team by `&[teamId+number]` (§3.4) and is the only identifier ENV-11 permits; carrying
 * the row id would put a second identifier on a screen that has no use for one, and FILE-3 defers
 * the detail view that would.
 */
export interface PlayerLink {
  readonly number: number;
  readonly foot: Foot | null;
  readonly notes: string | null;
}

/**
 * Every field {@link PlayerLink} is allowed to carry, as a runtime list.
 *
 * `satisfies` proves each entry is really a key. The alias below closes the other direction. Both
 * are needed: neither half alone stops a `name` from being added.
 */
export const PLAYER_LINK_FIELDS = ['number', 'foot', 'notes'] as const satisfies readonly (keyof PlayerLink)[];

type AssertTrue<T extends true> = T;

/**
 * ENV-11's build-failing half: a field added to {@link PlayerLink} and not to
 * {@link PLAYER_LINK_FIELDS} makes this conditional resolve to `false`, which fails the `extends
 * true` constraint and breaks the build. No runtime footprint.
 */
export type PlayerLinkFieldsAreClosed = AssertTrue<
  [Exclude<keyof PlayerLink, (typeof PLAYER_LINK_FIELDS)[number]>] extends [never] ? true : false
>;

/**
 * How a player is named on screen — **the only way one is named anywhere** (ENV-11).
 *
 * A number alone reads as a quantity next to a sentence about a pattern (*"7 cuts inside"* could be
 * a minute, a count or a shirt), so the `#` is not decoration: it is what makes the number legible
 * as an identifier without inventing anything to identify it by.
 */
export function playerLabel(link: PlayerLink): string {
  const foot = link.foot === null ? '' : ` · ${link.foot} foot`;
  return `#${String(link.number)}${foot}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// SES-11 / SES-6 — what Home lists, and what it must not
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SES-11's pending-distillation queue, in one predicate.
 *
 * > §10.4: *"the **pending-distillation queue** (SES-11 — every non-abandoned match in `fullTime`,
 * > each offering *Distill now*)"*
 *
 * Both clauses are load-bearing and the second is a MUST NOT: §2.2 rules that Abandon lands in
 * `fullTime` with `abandoned = true` precisely so it fires SYN-1's push, and SES-6 then requires
 * that it stay out of this queue *"unless the user opens one explicitly"* — otherwise the app would
 * ask, every launch, whether the user would like to distil the match they walked away from.
 *
 * An abandoned match is **not** hidden: §10.4 keeps it in the Team view with an *Abandoned* label
 * and a *Distill* action, which is what "opens one explicitly" means. See {@link distillEntry}.
 */
export function isPendingDistillation(match: MatchRow): boolean {
  return match.state === MATCH_STATE.fullTime && !match.abandoned;
}

/**
 * Matches part-way through distillation — **a second group, deliberately not the queue.**
 *
 * ── Declared reading (§16.4) ────────────────────────────────────────────────
 *
 * `machine.ts`'s transition table clears `activeMatch` at `FullTime`/`Abandon` and leaves it
 * `unchanged` for `EnterTriage`, `PassTriage` and `Defer`. So a match sitting in `triage` or
 * `distill` has **no resume pointer**, and §2.5's `resolveResume` returns `home` for it. If Home
 * showed only the `fullTime` queue, a user who entered triage and closed the app would find that
 * match nowhere on the screen they land on.
 *
 * Widening {@link isPendingDistillation} to cover them was rejected: §10.4 defines the queue by its
 * membership, SES-6's exclusion is stated against *that* queue, and a queue that grew a second
 * meaning would make the exclusion ambiguous. A separate group keeps §10.4's sentence exactly true
 * and closes the reachability hole — and `repos/matches.listMatchesByState` already documents Home
 * as reading `fullTime`/`triage`/`distill`.
 *
 * `abandoned` is **not** filtered here, and that is SES-6 being honoured rather than bypassed: a
 * match only reaches `triage` by the user opening it, which is the explicit act SES-6 exempts.
 */
export function isDistillationInProgress(match: MatchRow): boolean {
  return match.state === MATCH_STATE.triage || match.state === MATCH_STATE.distill;
}

/** What tapping a match on Home or in the Team view does. `null` — nothing; the match is not distillable yet. */
export type DistillEntry = 'start' | 'resumeTriage' | 'resumeDistill';

/**
 * SES-11 — *"entering `triage`/`distill` for any of them is available at any time from Home."*
 *
 * One function rather than a branch per surface, because Home's queue, Home's in-progress group and
 * the Team view's per-match action are the same decision seen three times, and the failure mode of
 * three copies is a screen that dispatches `EnterTriage` against a match already in `distill`.
 *
 * `archived` returns `null`: `CompleteDistillation` is terminal in §2.2 and there is no event out of
 * it. `preMatch` and the in-match states return `null` because distillation of a running match is
 * not a thing the state machine can express (§2.3).
 */
export function distillEntry(match: MatchRow): DistillEntry | null {
  if (match.state === MATCH_STATE.fullTime) return 'start';
  if (match.state === MATCH_STATE.triage) return 'resumeTriage';
  if (match.state === MATCH_STATE.distill) return 'resumeDistill';
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// FILE-1 — what the Team view lists, and in what order
// ─────────────────────────────────────────────────────────────────────────────

/**
 * FILE-1 as amended in revision 2 (§19 F-20) — *"both `confirmed` and `invalidated` hypotheses
 * across matches, the latter distinctly labeled."*
 *
 * `tracking` and `disproved` are absent by design: PRE-5 offers exactly these two as pre-match
 * board candidates, and F-20's whole finding was that a candidate the user cannot look up in the
 * team's history is not usable. Listing the other two would bury them.
 */
export type FileStatus = Extract<Status, 'confirmed' | 'invalidated'>;

/** The two, as values, so a caller need not spell them. */
export const FILE_STATUSES = ['confirmed', 'invalidated'] as const satisfies readonly FileStatus[];

/** Narrowing predicate — the one place FILE-1's status filter is decided. */
export function isFileStatus(status: Status): status is FileStatus {
  return status === 'confirmed' || status === 'invalidated';
}

/**
 * FILE-3's history ordering — *"newest first by calendar date"*, made a **total** order.
 *
 * `Match.date` is `YYYY-MM-DD` and two matches against one opponent can share a date (a tournament
 * morning). Falling through to `id` — `crypto.randomUUID()` — would render the same two matches in
 * a different order on different launches, which is the defect §3.2's `createdAt` column was added
 * to fix on the Discovery board. `MatchRow` has no `createdAt`, so `id` is the only remaining term;
 * it is at least **stable**, which is what the requirement actually needs, and the tie is recorded
 * here rather than left to `Array.prototype.sort`'s implementation.
 */
export function byMatchHistory(a: MatchRow, b: MatchRow): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The display order for a match's talk points **across both phases** (§10.4).
 *
 * `repos/talkPoints.listForMatch` sorts by `rank`, which is total **within** a phase — DIST-2
 * rewrites `1..n` per phase — and therefore not total across them: a halftime adjustment and a
 * full-time instruction both hold `rank: 1`, and a bare rank sort leaves those two in whatever
 * order `where('matchId')` happens to return, which is a key order and not a promise. The Team view
 * is the first surface that renders both together, so this is where the missing term is supplied.
 *
 * Halftime first, then full time: that is the order they were authored in and the order they were
 * said to the team in. `id` is the final term for `byMatchHistory`'s reason — stability beats
 * meaning when there is no meaning left to have.
 */
export function byTalkPointDisplay(
  a: { readonly phase: ArtifactPhase; readonly rank: number; readonly id: string },
  b: { readonly phase: ArtifactPhase; readonly rank: number; readonly id: string },
): number {
  if (a.phase !== b.phase) return a.phase === ARTIFACT_PHASE.halftime ? -1 : 1;
  if (a.rank !== b.rank) return a.rank - b.rank;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * How a match's lifecycle reads on the opponent file.
 *
 * In `domain/` because `MatchState` values may only be spelled here (§2.1, and the
 * `ses-1/match-state-literals` lint rule) — a label table in `ui/` would be a hand-rolled state
 * grouping, which is the exact thing that rule exists to refuse.
 *
 * `abandoned` wins over the state, and that is §2.2's ruling rendered: an abandoned match *is*
 * `fullTime`, so a label keyed on the state alone would call it *Full time* and the user would have
 * no way to tell it from a match that ran its ninety minutes.
 */
export function matchStateLabel(match: MatchRow): string {
  if (match.abandoned) return 'Abandoned';
  switch (match.state) {
    case MATCH_STATE.idle:
      return 'Not started';
    case MATCH_STATE.preMatch:
      return 'Setup';
    case MATCH_STATE.firstHalf:
      return 'First half';
    case MATCH_STATE.halftime:
      return 'Half time';
    case MATCH_STATE.secondHalf:
      return 'Second half';
    case MATCH_STATE.fullTime:
      return 'Full time';
    case MATCH_STATE.triage:
      return 'In triage';
    case MATCH_STATE.distill:
      return 'Distilling';
    case MATCH_STATE.archived:
      return 'Archived';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-6 — save board as template
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-6's window, and its **negative** — *"Not available during an in-match state."*
 *
 * > §6.0: *"On a `fullTime`/`triage`/`distill`/`archived` match, a *Save board as template* action
 * > creates a `Template` with `builtin = false` from that match's positioned hypotheses … it is a
 * > post-match action and would add a control to a committed layout for no in-match benefit."*
 *
 * The four states are listed positively rather than derived as "not in-match", because `preMatch`
 * and `idle` are neither in-match nor post-match and a negated predicate would admit both — a
 * template saved from an unstarted setup is a copy of the template library entry it came from.
 *
 * The same predicate gates the handler and the control, so §17's *"a structural proxy is not
 * acceptance"* has a rendered negative to check: on a match in a half, the action is not in the DOM
 * **and** the command refuses.
 */
export function canSaveBoardAsTemplate(state: MatchState): boolean {
  return (
    state === MATCH_STATE.fullTime ||
    state === MATCH_STATE.triage ||
    state === MATCH_STATE.distill ||
    state === MATCH_STATE.archived
  );
}

/**
 * PRE-6 — *"from that match's positioned hypotheses"*, in board order.
 *
 * ── Why the entries carry no `slots` ────────────────────────────────────────
 *
 * A PRE-4 stub's `slots` describe blanks that have **already been filled**: §6.0 resolves them by
 * substituting the picked values into `text` before the `Hypothesis` is created, so a board row
 * reads *"#7 always finishes left foot"* and there is nothing left to pick. Re-deriving slots by
 * pattern-matching the digits back out would produce a template that asks next week's user to fill
 * in blanks this week's user already answered — and would guess wrong on any text containing a
 * number that is not a shirt.
 *
 * ── Why `boardPosition` order and not `discoveryOrder` ──────────────────────
 *
 * A template is a **selection**, not a ranking: §6.2 assigns `boardPosition` `0..k−1` in the order
 * the user chose the patterns, and that is the order they will read them in next time. Count-derived
 * order would sort next season's template by how often a pattern happened to fire in one match.
 */
export function templateEntriesFromBoard(rows: readonly HypothesisRow[]): TemplateEntry[] {
  return rows
    .filter((row): row is HypothesisRow & { boardPosition: number } => row.boardPosition !== null)
    .sort((a, b) => a.boardPosition - b.boardPosition)
    .map((row) => ({
      text: row.text,
      phase: row.phase,
      discoveryBucket: row.discoveryBucket,
    }));
}
